import { AfterViewInit, Directive, forwardRef, OnDestroy, Optional } from '@angular/core';
import { MatLegacySelect as MatSelect } from '@angular/material/legacy-select';
import { FormViewAdapter, NGRX_FORM_VIEW_ADAPTER } from 'ngrx-forms';
import { Subscription } from 'rxjs';

/**
 * necessary since material 2 does not properly export the mat-select as a NG_VALUE_ACCESSOR
 */
/* eslint-disable @angular-eslint/directive-selector */
/* eslint-disable @angular-eslint/directive-class-suffix */
/* eslint-disable @typescript-eslint/no-explicit-any */
@Directive({
  selector: 'mat-select[ngrxFormControlState]',
  providers: [
    {
      provide: NGRX_FORM_VIEW_ADAPTER,
      useExisting: forwardRef(() => NgrxMatSelectViewAdapter),
      multi: true
    }
  ]
})
export class NgrxMatSelectViewAdapter<T> implements FormViewAdapter, AfterViewInit, OnDestroy {
  private value: T;
  private readonly subscriptions: Subscription[] = [];

  constructor(@Optional() private readonly matSelect?: MatSelect) {}

  ngAfterViewInit() {
    if (!this.matSelect) {
      return;
    }
    const s = this.matSelect;
    this.subscriptions.push(
      s.options.changes.subscribe(() => {
        Promise.resolve().then(() => s.writeValue(this.value));
      })
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  setViewValue(value: T) {
    this.value = value;

    if (!this.matSelect) {
      return;
    }
    const s = this.matSelect;

    // we have to verify that the same value is not set again since that would
    // cause focus to get lost on the select since it tries to focus the active option
    const selectedOption = s.selected;

    if (selectedOption) {
      if (Array.isArray(selectedOption) && Array.isArray(value)) {
        if (
          value.length === selectedOption.length &&
          value.every((v, i) => v === selectedOption[i])
        ) {
          return;
        }
      } else if (!Array.isArray(selectedOption)) {
        if (value === selectedOption.value) {
          return;
        }
      }
    }

    // because the options are potentially updated AFTER the value (because of their order in the DOM),
    // setting the value has to be deferred, otherwise we might select an option which is not available yet.
    Promise.resolve().then(() => s.writeValue(value));
  }

  setOnChangeCallback(fn: any) {
    if (!this.matSelect) {
      return;
    }
    this.matSelect.registerOnChange(value => {
      this.value = value;
      fn(value);
    });
  }

  setOnTouchedCallback(fn: any) {
    if (!this.matSelect) {
      return;
    }
    this.matSelect.registerOnTouched(fn);
  }

  setIsDisabled(isDisabled: boolean) {
    if (!this.matSelect) {
      return;
    }
    this.matSelect.setDisabledState(isDisabled);
  }
}
